import type { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';

import type { MPLPreApprovalOffer } from 'commonTypes/api/scenarios/CreditSelectionScreenContent';

import { isMplPreApprovalOffer } from 'shared/lib';
import {
  sendEventOnChangeMplPreApprovalDeliveryFormField,
  sendEventOnSubmitMplPreApprovalDeliveryFormField,
  sendEventPreApprovedMeetingModalClick,
  sendEventPreApprovedMeetingModalShow,
} from 'shared/lib/sendGAEvents';
import type { TOffer } from 'shared/types/TRankingService';

import { getAnalyticsProductParams } from 'entities/analytics';

import type { DeliveryStep } from '../ui/DeliveryStep';

import { useCommonMplAnalyticsParams } from './useCommonMplAnalyticsParams';

type DeliveryFormEvents = Pick<ComponentProps<typeof DeliveryStep>, 'onChangeEvent' | 'onShowEvent' | 'onSubmitEvent'>;

export const useDeliveryStepAnalyticsEvents = (offer: TOffer | MPLPreApprovalOffer): DeliveryFormEvents => {
  const commonMplAnalyticsParams = useCommonMplAnalyticsParams();

  const onChangeEvent: NonNullable<DeliveryFormEvents['onChangeEvent']> = useCallback(
    (offerStepData, formData, fieldName) => {
      const { bankName, productId, bankId, preApprovalStatus, productName } = offerStepData || {};
      const { offerType } = getAnalyticsProductParams(offer);

      if (isMplPreApprovalOffer(offer)) {
        sendEventOnChangeMplPreApprovalDeliveryFormField({
          ...commonMplAnalyticsParams,
          organizationName: bankName,
          productId,
          organizationId: bankId,
          address: formData.address,
          date: formData.dateSlot || '',
          time: formData.timeSlot || '',
          offerType,
        });
        return;
      }

      if (fieldName === 'address') {
        sendEventPreApprovedMeetingModalClick({
          bankName,
          offerName: productName,
          offerStatus: preApprovalStatus || '',
          action: 'Выбор адреса',
        });
        return;
      }
      sendEventPreApprovedMeetingModalClick({
        bankName,
        offerName: productName,
        offerStatus: preApprovalStatus || '',
        action: fieldName === 'dateSlot' ? 'Выбор даты' : 'Выбор времени',
      });
    },
    [commonMplAnalyticsParams, offer],
  );

  const onSubmitEvent: NonNullable<DeliveryFormEvents['onSubmitEvent']> = useCallback(
    (offerStepData, formData) => {
      const { bankName, preApprovalStatus, productName, productId, bankId } = offerStepData || {};
      const { offerType } = getAnalyticsProductParams(offer);

      if (isMplPreApprovalOffer(offer)) {
        sendEventOnSubmitMplPreApprovalDeliveryFormField({
          ...commonMplAnalyticsParams,
          organizationName: bankName,
          productId,
          organizationId: bankId,
          address: formData.address,
          date: formData.dateSlot || '',
          time: formData.timeSlot || '',
          offerType,
        });
        return;
      }

      sendEventPreApprovedMeetingModalClick({
        bankName,
        offerName: productName,
        offerStatus: preApprovalStatus || '',
        action: 'Подтвердить',
      });
    },
    [commonMplAnalyticsParams, offer],
  );

  const onShowEvent: NonNullable<DeliveryFormEvents['onShowEvent']> = useCallback(
    (offerStepData) => {
      if (isMplPreApprovalOffer(offer)) {
        return;
      }

      const { bankName, preApprovalStatus, productName } = offerStepData || {};
      sendEventPreApprovedMeetingModalShow({
        bankName,
        offerName: productName,
        offerStatus: preApprovalStatus || '',
      });
    },
    [offer],
  );

  return useMemo(
    () => ({
      onChangeEvent,
      onSubmitEvent,
      onShowEvent,
    }),
    [onChangeEvent, onSubmitEvent, onShowEvent],
  );
};
